import { BalloonIcon, ResponsiveOutlineIcon } from '@carboncopyco/icons';
import { isDesktop } from '@carboncopyco/mimesis-shared';
import type { ScreenResolution, Tour, TourFrame } from '@carboncopyco/types';
import {
  Button,
  ColorHex,
  Navbar,
  Separator,
  Spacer,
  TextBase,
  TextCaption,
  Tooltip,
  TooltipBubble,
} from '@carboncopyco/ui';
import { AnalyticsTrackEventName, useAnalytics } from '@carboncopyco/utils';
import { useBoolean } from 'ahooks';
import { useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useTourDispatch, useTourSelector } from '../../hooks';
import { selectIsMediaUploading, setActiveFrameId } from '../../store';
import type { UseNavigate } from '../../types/dependencies';
import { PublishTourModal } from './PublishTourModal';
import { StatusIndicator } from './StatusIndicator';
import { useTourEditorPageProps } from './TourEditorPageContext';
import useFrameWarnings from './components/FrameList/Frame/useFrameWarnings';

interface TourEditorNavbarProps {
  tour?: Tour;
  isSomeFrameWithNoSteps?: boolean;
  isTourModified: boolean;
  isPublishInProgress?: boolean;
  entityName: string;
  draftSettings: Tour['draftSettings'];
  onDiscard?: () => void;
  onPublishTour?: () => void;
  useNavigate: UseNavigate;
  onNavbarClick: () => void;
  refetchFrames: () => Promise<unknown>;
  dimension?: ScreenResolution;
  onPublishAndExit?: () => void;
}

export const TourEditorNavbar = ({
  tour,
  isSomeFrameWithNoSteps,
  isTourModified,
  isPublishInProgress,
  entityName,
  draftSettings,
  onDiscard,
  onPublishTour,
  useNavigate,
  onNavbarClick,
  refetchFrames,
  dimension,
  onPublishAndExit,
}: TourEditorNavbarProps) => {
  const { uuid } = useTourEditorPageProps();
  const { trackEvent } = useAnalytics();
  const [publishModalVisible, setPublishModalVisible] = useBoolean();
  const publishTourRef = useRef<HTMLButtonElement>(null);
  const { navigateToTourPreview } = useNavigate();
  const { tourId } = useParams<{
    tourId: string;
    demoId: string;
  }>();
  const { recalculateWarnings } = useFrameWarnings();
  const dimensionRef = useRef(null);
  const isResponsiveAspectRatio = draftSettings?.responsiveness;
  const isMediaUploading = useTourSelector(selectIsMediaUploading(uuid));
  const isPreviewDisabled = isMediaUploading;

  const dispatch = useTourDispatch();

  const handlePublishTour = () => {
    refetchFrames().then(({ data }: { data: TourFrame[] }) => {
      if (!draftSettings.showTooltips) {
        // Since on save, frameIds got changed, we need to recalculate warnings
        recalculateWarnings(data);
      }

      const newActiveFrameId = data?.find(
        (frame) => frame.isActive && !frame.isDisabled
      ).id;

      dispatch(
        setActiveFrameId({
          uuid,
          frameId: newActiveFrameId,
        })
      );
    });

    onPublishTour();
  };

  return (
    <div onClick={onNavbarClick}>
      <Navbar>
        <Navbar.Icon
          icon={
            <BalloonIcon
              size={30}
              color={ColorHex.natural50}
            />
          }
          bgColor="bg-ds-accent1-300"
        />
        <Navbar.PageDetails title={entityName} />
        <div className="flex-grow" />
        {isSomeFrameWithNoSteps && (
          <>
            <StatusIndicator
              label="There are frames with no steps"
              danger
            />
            <Spacer amount={2.5} />
            <Separator middle />
          </>
        )}
        <Navbar.ButtonGroup>
          {dimension?.width && (
            <div className="flex flex-row items-center mr-2">
              <ResponsiveOutlineIcon color={ColorHex.natural300} />
              <div ref={dimensionRef}>
                <TextBase
                  label={
                    dimension.type === 'responsive' && isResponsiveAspectRatio
                      ? 'Responsive'
                      : `${dimension.width} x ${dimension.height}px`
                  }
                />
              </div>
              <Tooltip
                placement="left"
                anchorRef={dimensionRef}
                shadow="md"
                offset={[0, 30]}
                noArrow
              >
                <div className="p-3">
                  <TextCaption
                    label="Tour's screen size."
                    color="text-ds-natural-300"
                    tagName="p"
                  />
                </div>
              </Tooltip>
            </div>
          )}
          {!isDesktop && (
            <Button
              label="Exit"
              link
              secondary
              onClick={onDiscard}
            />
          )}
          <Button
            label="Preview"
            disabled={isPreviewDisabled}
            secondary
            onClick={() => {
              trackEvent(AnalyticsTrackEventName.TOUR_CLICK_PREVIEW, {
                location: 'tour_editor_navbar',
                tourId,
              });
              setPublishModalVisible.setFalse();
              navigateToTourPreview(tourId);
            }}
          />

          <TooltipBubble
            title="Add tooltip or hide empty frame to save"
            placement="bottom"
            disabled={!isSomeFrameWithNoSteps}
          >
            <Button
              label="Save"
              disabled={isSomeFrameWithNoSteps || !isTourModified}
              onClick={() => {
                trackEvent(AnalyticsTrackEventName.TOUR_CLICK_SAVE, {
                  location: 'tour_editor_navbar',
                  tourId,
                });

                setPublishModalVisible.toggle();
              }}
              ref={publishTourRef}
            />
          </TooltipBubble>
        </Navbar.ButtonGroup>
        <PublishTourModal
          buttonRef={publishTourRef}
          onClose={setPublishModalVisible.setFalse}
          visible={publishModalVisible}
          tourId={Number(tourId)}
          tourName={tour?.tourName}
          onPublishTour={handlePublishTour}
          isTourModified={isTourModified}
          isPublishInProgress={isPublishInProgress}
          useNavigate={useNavigate}
          onPublishAndExit={onPublishAndExit}
        />
      </Navbar>
    </div>
  );
};
